/**
 * skillMatcher.js
 * --------------------------------------------------------------------------
 * Computes a skill-fit score given a profile's detected skills and a
 * target role. Returns four classifications:
 *
 *   - matched:   role-required skills the user has directly (full credit)
 *   - adjacent:  role-required skills the user has via an adjacent skill
 *                (partial credit, see ADJACENCY_WEIGHT)
 *   - missing:   role-required skills with no direct or adjacent evidence
 *   - bonus:     skills the user has that the role doesn't require
 *
 * Each adjacent match carries a `via` field naming the user's actual skill
 * and a `justification` string for the human-readable "why". This makes
 * the matcher's reasoning auditable per EU AI Act Art. 13 (Transparency)
 * and Art. 14 (Human Oversight).
 *
 * Adjacency map is hand-curated, not AI-generated, see skillAdjacency.js.
 */

import { ROLES, SKILL_BY_ID, labelFor } from '../data/skillOntology';
import { ADJACENCY_WEIGHT, findBestAdjacency } from '../data/skillAdjacency';
import { logAiEvent } from './aiAuditLog';

export function matchProfile(detectedSkillIds, role, opts = {}) {
  if (!role || !role.requiredSkillIds) {
    return { matched: [], adjacent: [], missing: [], bonus: detectedSkillIds || [], scorePercent: 0 };
  }
  const detected = new Set(detectedSkillIds || []);
  const required = new Set(role.requiredSkillIds);

  const matched  = []; // skillIds
  const adjacent = []; // { skillId, via, viaLabel?, strength, justification }
  const missing  = []; // skillIds (no direct match, no adjacency)

  // Track which detected skills have already been consumed as an adjacency
  // for some other required skill. Each detected skill can only contribute
  // once, either as a direct match, or as the bridge for one adjacency.
  // Without this, a single skill (e.g. Enterprise Sales) could be claimed
  // as adjacent for three different requirements and inflate the score.
  const consumedVias = new Set();

  for (const reqId of role.requiredSkillIds) {
    if (detected.has(reqId)) {
      matched.push(reqId);
      continue;
    }
    // Find the best adjacency that hasn't already been consumed
    const adj = findBestAdjacency(reqId, detected, consumedVias);
    if (adj) {
      consumedVias.add(adj.skillId);
      adjacent.push({
        skillId: reqId,
        via: adj.skillId,
        strength: adj.strength,
        justification: adj.justification,
      });
    } else {
      missing.push(reqId);
    }
  }

  // Bonus skills: anything detected that isn't a direct match AND isn't
  // already serving as an adjacency for a required skill.
  const bonus = (detectedSkillIds || []).filter(
    (id) => !required.has(id) && !consumedVias.has(id)
  );

  // Score: each direct match worth 1.0, each adjacency worth ADJACENCY_WEIGHT.
  // Score is the credited points / total required.
  const credit = matched.length + adjacent.length * ADJACENCY_WEIGHT;
  const scorePercent = role.requiredSkillIds.length === 0
    ? 0
    : Math.round((credit / role.requiredSkillIds.length) * 100);

  // Audit logging, every match scored is recorded for AI Act Art. 12.
  if (!opts.silent) {
    logAiEvent({
      operation: 'skill_match',
      roleId: role.id,
      detectedSkillCount: (detectedSkillIds || []).length,
      requiredSkillCount: role.requiredSkillIds.length,
      matchedCount: matched.length,
      adjacentCount: adjacent.length,
      scorePercent,
    });
  }

  return { matched, adjacent, missing, bonus, scorePercent };
}

export function getRole(roleKey) {
  return ROLES[roleKey] || null;
}

export function formatSkillLabel(skillId, lang = 'en') {
  return labelFor(SKILL_BY_ID[skillId], lang);
}
